import { createApp } from 'vue'
import { createPinia } from 'pinia'
import Vue3Toastify, { type ToastContainerOptions } from 'vue3-toastify'

import App from './App.vue'
import router, { getLastDashboardRoute, clearLastDashboardRoute } from './router'
import { useAuthStore } from '@/stores/auth'
import { realtimeClient } from '@/lib/realtimeservice'
import { mountAlertModal, overrideBrowserAlerts } from '@/utils/alert-override'
import { initializeSupervisorPostMessageBridge } from '@/supervisor/postmessage-bridge'
import { initializeSupervisorDevHelper } from '@/supervisor/dev-helper'

import './assets/main.css'
import 'vue3-toastify/dist/index.css'

const app = createApp(App)
const pinia = createPinia()

// Mount alert modal and override browser alerts
mountAlertModal()
overrideBrowserAlerts()
initializeSupervisorPostMessageBridge()
initializeSupervisorDevHelper()

app.use(pinia)
app.use(router)

// Configure toast notifications
app.use(Vue3Toastify, {
  autoClose: 3000,
  position: 'top-right',
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: 'colored',
  transition: 'slide',
  hideProgressBar: false,
  closeButton: true,
  limit: 5, // Maximum number of toasts to show at once
} as ToastContainerOptions)

// Initialize auth on app start
const authStore = useAuthStore()

// Handle route restoration for authenticated users
const handleRouteRestoration = async () => {
  const lastDashboardRoute = getLastDashboardRoute()
  const isAuthenticated = authStore.isAuthenticated
  const currentPath = window.location.pathname
  
  console.log('Route restoration check:', {
    currentPath,
    lastDashboardRoute,
    isAuthenticated,
    shouldCheck: isAuthenticated && lastDashboardRoute && lastDashboardRoute !== currentPath,
    isHomepage: currentPath === '/' || currentPath === '/waitlist'
  })
  
  // Only restore dashboard routes, never redirect from homepage
  if (isAuthenticated && lastDashboardRoute && lastDashboardRoute !== currentPath) {
    // CRITICAL: Only restore if we're on the homepage (not auth or team invite pages)
    if (currentPath === '/' || currentPath === '/waitlist') {
      console.log('Restoring last dashboard route:', lastDashboardRoute)
      await router.replace(lastDashboardRoute)
      return true
    } else if (currentPath.startsWith('/team-invite/')) {
      console.log('On team invite page - skipping route restoration to allow invitation processing')
      return false
    } else {
      console.log('Not restoring - not on homepage, current path:', currentPath)
    }
  }
  return false
}

// Load auth state - but not on auth callback to prevent conflicts
if (!window.location.pathname.includes('/auth/callback')) {
  // SIMPLE FIX: Check if we should restore dashboard route
  const currentPath = window.location.pathname
  const lastDashboardRoute = getLastDashboardRoute()
  const shouldRestore = sessionStorage.getItem('should_restore_dashboard') === 'true'
  
  console.log('Route restoration logic:', {
    currentPath,
    lastDashboardRoute,
    shouldRestore,
    isAuthenticated: authStore.isAuthenticated
  })
  
  // Normal navigation or route restoration - always load user data
  authStore.loadUser()

  // CRITICAL: Never restore routes if user is on team invite page - allow them to process the invitation
  if (currentPath.startsWith('/team-invite/')) {
    console.log('On team invite page - skipping route restoration to allow invitation processing')
  }
  // CRITICAL: Never restore routes if user is on OAuth callback page - allow the callback to process
  else if (currentPath.includes('/oauth-callback') || currentPath.includes('/github-callback')) {
    console.log('On OAuth callback page - skipping route restoration to allow OAuth processing')
  }
  // Only restore dashboard route if we're on a page that should be restored (not homepage, waitlist, or team invite)
  else if (authStore.isAuthenticated && lastDashboardRoute && shouldRestore && currentPath !== '/' && currentPath !== '/waitlist') {
    console.log('Restoring dashboard route after refresh:', lastDashboardRoute)
    sessionStorage.removeItem('should_restore_dashboard')
    router.isReady().then(() => {
      router.replace(lastDashboardRoute)
    })
  }
}

app.mount('#app')
